import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  startIndex: number;
  endIndex: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
}

const Pagination = ({
  currentPage,
  totalPages,
  totalCount,
  startIndex,
  endIndex,
  onPageChange,
  maxVisiblePages = 5,
}: PaginationProps) => {
  // 표시할 페이지 번호들을 계산
  const getVisiblePages = () => {
    const pages: number[] = [];
    
    if (totalPages <= maxVisiblePages) {
      // 전체 페이지가 최대 표시 개수보다 적거나 같으면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 현재 페이지를 중심으로 페이지 번호 계산
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      // 끝 페이지가 조정되면 시작 페이지도 재조정
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  if (totalPages <= 1) {
    return (
      <div className="px-4 py-4 border-t border-slate-800 flex items-center justify-between">
        <p className="text-sm text-slate-400">
          {startIndex + 1}-{endIndex} / 총 {totalCount}개
        </p>
        <div></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 border-t border-slate-800 flex flex-wrap items-center gap-y-2 justify-between">
      <p className="text-sm text-slate-400">
        {startIndex + 1}-{endIndex} / 총 {totalCount}개
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {/* 이전 버튼 */}
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="이전 페이지"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* 첫 페이지 */}
        {visiblePages[0] > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className="px-3 py-1 rounded-lg transition-colors bg-slate-800 text-slate-400 hover:bg-slate-700"
            >
              1
            </button>
            {visiblePages[0] > 2 && (
              <span className="text-slate-500 px-2">...</span>
            )}
          </>
        )}

        {/* 페이지 번호들 */}
        {visiblePages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded-lg transition-colors ${
              currentPage === page
                ? "bg-purple-500 text-white"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            {page}
          </button>
        ))}

        {/* 마지막 페이지 */}
        {visiblePages[visiblePages.length - 1] < totalPages && (
          <>
            {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
              <span className="text-slate-500 px-2">...</span>
            )}
            <button
              onClick={() => onPageChange(totalPages)}
              className="px-3 py-1 rounded-lg transition-colors bg-slate-800 text-slate-400 hover:bg-slate-700"
            >
              {totalPages}
            </button>
          </>
        )}

        {/* 다음 버튼 */}
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="다음 페이지"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination; 